import { Select } from "@JohnLewisPartnership/jl-design-system/dist/components/select/Select";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { dispatchNewEvent } from "@/utils/analytics/trigger/analyticsTrigger";
import EmailLiveChat from "../email-live-chat/EmailLiveChat";
import styles from "./email-questions.module.scss";

function EmailQuestions({ onFinished, emailData, isLiveChatInEmailUsEnabled = false }) {
  const router = useRouter();
  const urlNodes = router.query?.t?.split("-");
  const hide = router.query?.from === "orders";
  const [questions, setQuestions] = useState([{ questionId: "subjects" }]);
  const [formData, setFormData] = useState(null);
  const [liveChatDiscarded, setLiveChatDiscarded] = useState(false);

  const buildQuestionsFromUrlNodes = () => {
    const questionsArr = [{ questionId: "subjects" }];
    let tempFormData;
    if (urlNodes) {
      urlNodes.forEach((n, i) => {
        questionsArr[i].answer = n;
        questionsArr.push({ questionId: n });
        // Check if question that contains form data is being loaded
        if (emailData[n]?.form) {
          setFormData(emailData[n].form);
          tempFormData = emailData[n].form;
        }
        // Check if it's last one
        if (!emailData[n]?.select && !emailData[n]?.nextQuestion) {
          onFinished({
            formData: tempFormData,
          });
        }
      });
      setLiveChatDiscarded(true);
    }

    setQuestions(questionsArr);
  };

  useEffect(() => {
    buildQuestionsFromUrlNodes();
  }, []);

  /**
   * TODO: Hardcoded timings for live chat hours
   *
   * This needs to be fixed to use the Contact Channel Manager
   * https://gitlab.com/JohnLewisPartnership/digital/services/customer-help/contact-channel-manager
   */
  const outOfOfficeHours = () => {
    const clientDate = new Date();
    const hours = clientDate.getUTCHours();
    const day = clientDate.getUTCDay();

    const isDuringWeek = day >= 1 && day <= 5;
    const isDuringOfficeHours = hours >= 10 && hours <= 18;

    return !isDuringWeek || !isDuringOfficeHours;
  };

  const handleQuestionAnswered = (questionId, answer) => {
    if (answer === "default") {
      return;
    }

    const buildNewQuestionsArr = () => {
      const result = [];
      let questionArrayComplete = false;
      let liveChatOffRampAlreadyDisplayed = false;

      questions.forEach(q => {
        if (questionArrayComplete === false) {
          if (q && emailData[q.questionId] && emailData[q.questionId].liveChat) {
            liveChatOffRampAlreadyDisplayed = true;
          }

          if (q.questionId !== questionId) {
            result.push({ ...q });
          } else {
            result.push({ questionId, answer });
            // Decision tree changed
            if (q.answer) {
              // Reset LiveChat state we have not previously run into an off-ramp in this question array
              if (!liveChatOffRampAlreadyDisplayed && liveChatDiscarded) {
                setLiveChatDiscarded(false);
              }
              onFinished(null);
            }
            questionArrayComplete = true;
          }
        }
      });
      return result;
    };

    const finish = (_questions, form) => {
      onFinished({
        formData: form || formData,
        shop: _questions.find(q => ["shop", "shopLostProperty", "returnsShop", "refundsShop"].includes(q.questionId))
          ?.answer,
      });
      router.query.t = _questions.reduce((acc, item) => `${acc}${acc ? "-" : ""}${item.answer}`, "");
      router.push(router, undefined, { shallow: true });
    };

    const newQuestions = buildNewQuestionsArr();

    // Analytics
    if (emailData[questionId]?.analyticsEvent) {
      dispatchNewEvent("dataStateChange", {
        action: emailData[questionId].analyticsEvent.action,
        type: answer,
        topic: answer,
      });
    }
    if (emailData[answer]?.form?.subject) {
      dispatchNewEvent("dataStateChange", {
        action: "email.topic",
        type: emailData[answer]?.form?.subject,
        topic: emailData[answer]?.form?.subject,
        ...emailData[answer]?.analyticsEvent,
      });
    }

    // It's last question
    if (
      !emailData[answer] ||
      (emailData[answer].form &&
        !emailData[answer].nextQuestion &&
        (!emailData[answer].liveChat ||
          liveChatDiscarded ||
          isLiveChatInEmailUsEnabled === false ||
          outOfOfficeHours() === true))
    ) {
      setQuestions(newQuestions);
      finish(newQuestions, emailData[answer]?.form);
    } else {
      // This question is not last but defines the form
      if (emailData[answer]?.form) {
        setFormData(emailData[answer].form);
      }

      setQuestions([...newQuestions, { questionId: answer }]);
    }
  };

  const renderQuestion = ({ questionId, answer }) => {
    if (
      isLiveChatInEmailUsEnabled &&
      emailData[questionId]?.liveChat &&
      !liveChatDiscarded &&
      outOfOfficeHours() === false
    ) {
      return (
        <EmailLiveChat
          questionId={questionId}
          liveChat={emailData[questionId].liveChat}
          onEmailClick={() => {
            setLiveChatDiscarded(true);
            if (!emailData[questionId].select && !emailData[questionId].nextQuestion) {
              handleQuestionAnswered(questionId, "");
            }
          }}
        />
      );
    }

    const selectData = emailData[questionId]?.select || emailData[emailData[questionId]?.nextQuestion]?.select;

    if (selectData) {
      return (
        <div className={styles["email-questions__field"]}>
          {selectData.label ? <label htmlFor={questionId}>{selectData.label}</label> : null}
          <Select
            id={questionId}
            name={questionId}
            onChange={evt => handleQuestionAnswered(questionId, evt.target.value)}
            value={answer}
          >
            {selectData.options
              .map(option => ({
                value: option.value || "default",
                label: option.label,
              }))
              .map(({ value, label }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
          </Select>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles["email-questions"]}>
      {!hide && (
        <div className={styles["email-questions__container"]}>
          {questions.map(q => (
            <React.Fragment key={q.questionId}>{renderQuestion(q)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

EmailQuestions.propTypes = {
  onFinished: PropTypes.func.isRequired,
  emailData: PropTypes.objectOf(PropTypes.object).isRequired,
  isLiveChatInEmailUsEnabled: PropTypes.bool,
};

export default EmailQuestions;
